import { useState, useCallback } from 'react';

export interface FilterState {
    timeWindow: string;
    selectedClients: string[];
    selectedDiagnosticCodes?: string[];
    selectedProcessors?: string[];
    startDateTime?: Date;
    endDateTime?: Date;
}

export interface FilterOptions {
    availableClients: string[];
    availableDiagnosticCodes?: string[];
    availableProcessors?: string[];
}

export const useFilterState = (initialState: Partial<FilterState> = {}) => {
    const [filters, setFilters] = useState<FilterState>({
        timeWindow: 'now-1y',
        selectedClients: ['AllClients'],
        selectedDiagnosticCodes: ['AllDiagnosticCodes'],
        selectedProcessors: ['AllProcessors'],
        startDateTime: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
        endDateTime: new Date(),
        ...initialState
    });

    const [filterOptions, setFilterOptions] = useState<FilterOptions>({
        availableClients: ['AllClients'],
        availableDiagnosticCodes: ['AllDiagnosticCodes'],
        availableProcessors: ['AllProcessors']
    });

    const updateFilter = useCallback(<K extends keyof FilterState>(
        key: K, 
        value: FilterState[K]
    ) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    const updateFilterOptions = useCallback((options: Partial<FilterOptions>) => {
        setFilterOptions(prev => ({ ...prev, ...options }));
    }, []);

    const resetFilters = useCallback((newState: Partial<FilterState> = {}) => {
        setFilters({
            timeWindow: 'now-1y',
            selectedClients: ['AllClients'],
            selectedDiagnosticCodes: ['AllDiagnosticCodes'],
            selectedProcessors: ['AllProcessors'],
            startDateTime: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
            endDateTime: new Date(),
            ...newState
        });
    }, []);

    return {
        filters,
        filterOptions,
        updateFilter,
        updateFilterOptions,
        resetFilters,
        setFilters
    };
};
